// ============================================================================
// 📦 VERSION: 2026.01.28.1400
// ============================================================================
// GroupQuizAdminPanel.jsx - Admin Settings for Group Quiz
// Controls: Ads, Quiz Defaults, System Settings
// Can be used standalone or integrated into CompleteGameAdminPanel.js
// ============================================================================
import React, { useState, useEffect } from 'react';

const API_URL = 'https://api.traktag.com/api';

const GroupQuizAdminPanel = ({
  onSettingsChange,
  initialSettings
}) => {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({
    activeQuizzes: '--',
    totalPlayers: '--',
    todayQuizzes: '--',
    adRevenue: '--'
  });

  // Settings state
  const [settings, setSettings] = useState({
    // Ads
    adsEnabled: true,
    adPages: {
      lobby: true,
      play: false,
      results: true,
      join: true,
      store: true
    },
    adFormats: {
      '320x50': true,
      '300x250': true,
      '728x90': false
    },
    
    // Quiz Defaults
    defaultAllowSharing: true,
    defaultAllowTeams: true,
    defaultMaxPlayers: 50,
    defaultQuestionTimer: 30,
    
    // System
    maintenanceMode: false,
    allowRegistrations: true,
    debugMode: true,
    
    ...initialSettings
  });

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        
        // Try API first
        const response = await fetch(`${API_URL}/admin/group-quiz/settings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        } else {
          // Fallback to localStorage
          const stored = localStorage.getItem('traktag_admin_settings');
          if (stored) {
            setSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
          }
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        // Fallback to localStorage
        const stored = localStorage.getItem('traktag_admin_settings');
        if (stored) {
          setSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
    loadStats();
  }, []);

  // Load stats
  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/group-quiz/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // Demo stats
        setStats({
          activeQuizzes: Math.floor(Math.random() * 20) + 5,
          totalPlayers: Math.floor(Math.random() * 500) + 100,
          todayQuizzes: Math.floor(Math.random() * 50) + 10,
          adRevenue: `£${(Math.random() * 50 + 10).toFixed(2)}`
        });
      }
    } catch (err) {
      // Demo stats on error
      setStats({
        activeQuizzes: Math.floor(Math.random() * 20) + 5,
        totalPlayers: Math.floor(Math.random() * 500) + 100,
        todayQuizzes: Math.floor(Math.random() * 50) + 10,
        adRevenue: `£${(Math.random() * 50 + 10).toFixed(2)}`
      });
    }
  };

  // Update setting
  const updateSetting = (key, value) => {
    setSettings(prev => {
      const updated = { ...prev, [key]: value };
      // Auto-save to localStorage
      localStorage.setItem('traktag_admin_settings', JSON.stringify(updated));
      // Notify parent if callback provided
      if (onSettingsChange) {
        onSettingsChange(updated);
      }
      return updated;
    });
  };

  // Update nested setting (adPages, adFormats)
  const updateNestedSetting = (parent, key, value) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        [parent]: {
          ...prev[parent],
          [key]: value
        }
      };
      localStorage.setItem('traktag_admin_settings', JSON.stringify(updated));
      if (onSettingsChange) {
        onSettingsChange(updated);
      }
      return updated;
    });
  };

  // Save all settings to API
  const saveAllSettings = async () => {
    try {
      setSaving(true);
      
      const response = await fetch(`${API_URL}/admin/group-quiz/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        showToast('✅ Settings saved successfully!');
      } else {
        // Still save to localStorage
        localStorage.setItem('traktag_admin_settings', JSON.stringify(settings));
        showToast('⚠️ Saved locally (API unavailable)');
      }
    } catch (err) {
      localStorage.setItem('traktag_admin_settings', JSON.stringify(settings));
      showToast('⚠️ Saved locally (API unavailable)');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    if (window.confirm('Reset all settings to defaults? This cannot be undone.')) {
      const defaults = {
        adsEnabled: true,
        adPages: { lobby: true, play: false, results: true, join: true, store: true },
        adFormats: { '320x50': true, '300x250': true, '728x90': false },
        defaultAllowSharing: true,
        defaultAllowTeams: true,
        defaultMaxPlayers: 50,
        defaultQuestionTimer: 30,
        maintenanceMode: false,
        allowRegistrations: true,
        debugMode: true
      };
      setSettings(defaults);
      localStorage.setItem('traktag_admin_settings', JSON.stringify(defaults));
      showToast('🔄 Settings reset to defaults');
    }
  };

  // Export settings
  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'traktag_group_quiz_settings.json';
    a.click();
    URL.revokeObjectURL(url);
    showToast('📤 Settings exported');
  };

  // Toast helper
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  // Toggle component
  const Toggle = ({ checked, onChange, disabled }) => (
    <button
      onClick={() => !disabled && onChange(!checked)}
      className={`
        relative w-12 h-6 rounded-full transition-colors
        ${checked ? 'bg-green-500' : 'bg-gray-400'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span className={`
        absolute top-1 w-4 h-4 bg-white rounded-full transition-transform
        ${checked ? 'left-7' : 'left-1'}
      `} />
    </button>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading settings...</div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚙️</span>
            <h1 className="text-xl font-bold text-white">Group Quiz Admin</h1>
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
              ADMIN ONLY
            </span>
          </div>
          <div className={`
            flex items-center gap-2 px-3 py-2 rounded-lg text-sm
            ${settings.maintenanceMode 
              ? 'bg-gray-700 text-gray-400' 
              : 'bg-green-900/50 text-green-400'}
          `}>
            <span className={`
              w-2 h-2 rounded-full
              ${settings.maintenanceMode ? 'bg-gray-400' : 'bg-green-400'}
            `} />
            {settings.maintenanceMode ? 'Maintenance Mode' : 'System Active'}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Active Quizzes', value: stats.activeQuizzes },
            { label: 'Players Online', value: stats.totalPlayers },
            { label: 'Quizzes Today', value: stats.todayQuizzes },
            { label: 'Ad Revenue', value: stats.adRevenue }
          ].map((stat, idx) => (
            <div key={idx} className="bg-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-purple-400">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Advertisement Settings */}
        <div className="bg-gray-800 rounded-xl p-5 mb-5 border border-gray-700">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-700">
            <span className="text-2xl">📢</span>
            <div>
              <h2 className="text-lg font-semibold text-white">Advertisement Settings</h2>
              <p className="text-xs text-gray-500">Control ad visibility across the platform</p>
            </div>
          </div>

          {/* Enable Ads */}
          <div className="flex items-center justify-between py-4 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Enable Advertisements</p>
              <p className="text-xs text-gray-500">Master toggle - turns all ads on/off globally</p>
            </div>
            <Toggle 
              checked={settings.adsEnabled} 
              onChange={(v) => updateSetting('adsEnabled', v)} 
            />
          </div>

          {/* Ad Pages */}
          <div className={`py-4 border-b border-gray-700 ${!settings.adsEnabled ? 'opacity-40' : ''}`}>
            <p className="text-white font-medium mb-3">Ad Placement Pages</p>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'lobby', label: '🏠 Lobby' },
                { key: 'play', label: '🎮 Play' },
                { key: 'results', label: '🏆 Results' },
                { key: 'join', label: '🚪 Join' },
                { key: 'store', label: '🛒 Store' }
              ].map(page => (
                <label 
                  key={page.key}
                  className={`
                    flex items-center gap-2 bg-gray-700 px-3 py-2 rounded-lg cursor-pointer
                    hover:bg-gray-600 transition-colors
                  `}
                >
                  <input
                    type="checkbox"
                    checked={settings.adPages?.[page.key] ?? false}
                    onChange={(e) => updateNestedSetting('adPages', page.key, e.target.checked)}
                    disabled={!settings.adsEnabled}
                    className="accent-green-500"
                  />
                  <span className="text-sm text-white">{page.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Ad Formats */}
          <div className={`py-4 ${!settings.adsEnabled ? 'opacity-40' : ''}`}>
            <p className="text-white font-medium mb-3">Ad Formats</p>
            <div className="flex flex-wrap gap-2">
              {[
                { key: '320x50', label: '320×50 Banner' },
                { key: '300x250', label: '300×250 Rectangle' },
                { key: '728x90', label: '728×90 Leaderboard' }
              ].map(format => (
                <label 
                  key={format.key}
                  className="flex items-center gap-2 bg-gray-700 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={settings.adFormats?.[format.key] ?? false}
                    onChange={(e) => updateNestedSetting('adFormats', format.key, e.target.checked)}
                    disabled={!settings.adsEnabled}
                    className="accent-green-500"
                  />
                  <span className="text-sm text-white">{format.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Quiz Default Settings */}
        <div className="bg-gray-800 rounded-xl p-5 mb-5 border border-gray-700">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-700">
            <span className="text-2xl">🎯</span>
            <div>
              <h2 className="text-lg font-semibold text-white">Quiz Default Settings</h2>
              <p className="text-xs text-gray-500">Defaults for new quizzes (QuizMasters can override)</p>
            </div>
          </div>

          {/* Allow Sharing */}
          <div className="flex items-center justify-between py-4 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Allow Sharing by Default</p>
              <p className="text-xs text-gray-500">Players can share quiz code & invite friends</p>
            </div>
            <Toggle 
              checked={settings.defaultAllowSharing} 
              onChange={(v) => updateSetting('defaultAllowSharing', v)} 
            />
          </div>

          {/* Allow Teams */}
          <div className="flex items-center justify-between py-4 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Allow Teams by Default</p>
              <p className="text-xs text-gray-500">Enable team play option for new quizzes</p>
            </div>
            <Toggle 
              checked={settings.defaultAllowTeams} 
              onChange={(v) => updateSetting('defaultAllowTeams', v)} 
            />
          </div>

          {/* Max Players */}
          <div className="flex items-center justify-between py-4 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Default Max Players</p>
              <p className="text-xs text-gray-500">Maximum participants per quiz</p>
            </div>
            <input
              type="number"
              min="2"
              max="1000"
              value={settings.defaultMaxPlayers}
              onChange={(e) => updateSetting('defaultMaxPlayers', parseInt(e.target.value) || 50)}
              className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center"
            />
          </div>

          {/* Question Timer */}
          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-white font-medium">Default Question Timer (seconds)</p>
              <p className="text-xs text-gray-500">Time allowed per question</p>
            </div>
            <input
              type="number"
              min="5"
              max="120"
              value={settings.defaultQuestionTimer}
              onChange={(e) => updateSetting('defaultQuestionTimer', parseInt(e.target.value) || 30)}
              className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-center"
            />
          </div>
        </div>

        {/* System Settings */}
        <div className="bg-gray-800 rounded-xl p-5 mb-5 border border-gray-700">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-700">
            <span className="text-2xl">🔧</span>
            <div>
              <h2 className="text-lg font-semibold text-white">System Settings</h2>
              <p className="text-xs text-gray-500">Platform-wide controls</p>
            </div>
          </div>

          {/* Maintenance Mode */}
          <div className="flex items-center justify-between py-4 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Maintenance Mode</p>
              <p className="text-xs text-gray-500">Block new quiz joins, show maintenance message</p>
            </div>
            <Toggle 
              checked={settings.maintenanceMode} 
              onChange={(v) => updateSetting('maintenanceMode', v)} 
            />
          </div>

          {/* Allow Registrations */}
          <div className="flex items-center justify-between py-4 border-b border-gray-700">
            <div>
              <p className="text-white font-medium">Allow New Registrations</p>
              <p className="text-xs text-gray-500">Allow new TRAKtag accounts to be created</p>
            </div>
            <Toggle 
              checked={settings.allowRegistrations} 
              onChange={(v) => updateSetting('allowRegistrations', v)} 
            />
          </div>

          {/* Debug Mode */}
          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-white font-medium">Debug Mode</p>
              <p className="text-xs text-gray-500">Show version badges and console logs</p>
            </div>
            <Toggle 
              checked={settings.debugMode} 
              onChange={(v) => updateSetting('debugMode', v)} 
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={saveAllSettings}
            disabled={saving}
            className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
          >
            {saving ? '⏳ Saving...' : '💾 Save All Settings'}
          </button>
          <button
            onClick={resetToDefaults}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
          >
            🔄 Reset to Defaults
          </button>
          <button
            onClick={exportSettings}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
          >
            📤 Export Settings
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-green-500 text-white px-5 py-3 rounded-xl font-medium shadow-lg animate-pulse">
          {toast}
        </div>
      )}

      {/* Version Badge */}
      {settings.debugMode && (
        <div className="fixed bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          GroupQuizAdminPanel.jsx v2026.01.28.1400
        </div>
      )}
    </div>
  );
};

export default GroupQuizAdminPanel;
